import { useCallback, useEffect, useState } from "react";
import LoadingWidget from "../../context_providers/modal/loader";
import moment from "moment";
import { UTCToLocalDate } from "../../utils/date_util";
import { mdiExport, mdiFilterVariant } from "@mdi/js";
import Icon from "@mdi/react";
import {
  POReportDefaultFilter,
  POReportFilterModel,
  POReportModel,
} from "./models/filter_model";
import { debounce, round } from "lodash";
import {
  exportPOReportData,
  getPoReportData,
  sendToTyno,
} from "./service/po_report_repo";
import { ToastType, useToast } from "../../context_providers/toast/toast_ctx";
import Pagination from "../../ui/pages/content_panel/ui/new_components/common/pagination";
import { urls } from "../../utils/urls";
import { formatAmountWithCommas } from "../../ui/pages/content_panel/utils/helpers";
import { useAuth } from "../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../utils/enums";
import { useHistory } from "react-router-dom";
import SearchFieldWidget from "../../ui/new_components/search_field";
import {
  ResponsiveListShell,
  FilterSheet,
  DateRangeFilter,
  MobileRecordCard,
  MobileRecordCardList,
  StatusBadge,
  EmptyState,
} from "../../ui/new_components/common/responsive";

const PurchaseOrderReportListScreen = () => {
  const [filter, setFilter] = useState<POReportFilterModel>(
    POReportDefaultFilter()
  );
  const { showToast } = useToast();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<POReportModel[]>([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date>();
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768
  );
  const { isAuthorised } = useAuth();
  const navigation = useHistory();

  const exportData = async () => {
    setLoading(true);
    try {
      filter.all = true;
      const res = await exportPOReportData(filter);

      if (!res.ok) {
        return showToast({ type: ToastType.error, text: res.statusText });
      }

      // Generate a filename
      let downloadFilename = "export.csv";
      const header = res.headers.get("Content-Disposition");
      const parts = header?.split(";");
      if (parts && parts.length > 1) {
        downloadFilename = parts[1].split("=")[1];
        downloadFilename = downloadFilename.substring(
          1,
          downloadFilename.length - 1
        );
      }
      const blob = await res.blob();

      const fileUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", downloadFilename ?? `export.csv`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    } catch (error: any) {
      console.log(error);
      showToast({ type: ToastType.error, text: "Download failed" });
    }
    setLoading(false);
  };

  const fetchData = async (prop: POReportFilterModel) => {
    setLoading(true);
    try {
      const res = await getPoReportData(prop);
      if (res.success) {
        setData(res.result.result);
        if (res.result.metadata) {
          setTotal(res.result.metadata.total);
        }
        setFetchedAt(new Date());
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((prop: POReportFilterModel) => {
      if (prop) fetchData(prop);
      else setData([]);
    }, 100),
    []
  );

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter]);

  const handleSendToTyno = async (item: any) => {
    if (loadingId) return; // Prevent multiple simultaneous sends

    try {
      //Case 1: PO is NOT approved — show info toast and skip API call
      if (item.approval_status !== "Approved") {
        showToast({
          type: ToastType.info,
          text: "Attachment uploaded successfully, but unapproved PO attachments cannot be sent to Tyno.",
        });
        return;
      }

      //Case 2: PO is approved — proceed with Tyno API call

      const tynoRes = await sendToTyno(item.po_no, item.id, item.attachment);

      if (tynoRes.success) {
        showToast({
          type: ToastType.success,
          text: tynoRes.message,
        });

        // Refresh data after successful send
        await fetchData(filter);
      } else {
        showToast({
          type: ToastType.error,
          text:
            "Upload succeeded but sending to Tyno failed: " +
            (tynoRes?.error || "Unknown error"),
        });
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: "Error sending to Tyno: " + err.message,
      });
    } finally {
      setLoadingId(null);
    }
  };

  useEffect(() => {
    const warehouse_operator_permission = isAuthorised({
      module: MODULE.warehouse_operator,
      action: ACTION.READ,
    });

    if (warehouse_operator_permission) {
      navigation.replace(urls.notAllowed);
    }
  }, []);

  const renderTynoStatus = (item: POReportModel) => {
    if (item.attachment?.category !== "Tax invoice") {
      return <span className="text-gray-400">-</span>;
    }
    if (item.status === "pending" || item.status === "failed") {
      return (
        <button
          className="bg-myPrimaryColor text-white px-2 py-1 rounded text-xs hover:opacity-90 min-w-[90px]"
          onClick={() => handleSendToTyno(item)}
          disabled={loadingId === item.id}
        >
          {loadingId === item.id ? "Sending..." : "Send to Tyno"}
        </button>
      );
    }
    return (
      <StatusBadge
        status={item.status}
        kind="tyno"
        tone={
          item.status === "parsed"
            ? "success"
            : item.status === "sent"
            ? "info"
            : undefined
        }
      />
    );
  };

  return (
    <>
      <ResponsiveListShell
        title="Purchase Orders Report"
        count={total}
        fetchedAt={fetchedAt}
        onRefresh={() => fetchData(filter)}
        search={
          <SearchFieldWidget
            placeholder="Search PO No."
            defaulValue={filter.po_no ?? ""}
            onClear={() => setFilter((o) => ({ ...o, po_no: "", page: 1 }))}
            onSubmit={(value) =>
              setFilter((o) => ({ ...o, po_no: value, page: 1 }))
            }
          />
        }
        actions={
          <button
            onClick={exportData}
            className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
          >
            <Icon path={mdiExport} className="h-4 w-4"></Icon>Export
          </button>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => setFilter(POReportDefaultFilter())}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div>
            <label>PO Date</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromDate={
                filter.fromDate
                  ? moment(filter.fromDate).format("YYYY-MM-DD")
                  : undefined
              }
              toDate={
                filter.toDate
                  ? moment(filter.toDate).format("YYYY-MM-DD")
                  : undefined
              }
              onFromChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, fromDate: null, page: 1 }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const fromDate = new Date(new Date(now).setHours(0, 0, 0, 0));
                setFilter((o) => ({ ...o, fromDate, page: 1 }));
              }}
              onToChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, toDate: null, page: 1 }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const toDate = new Date(
                  new Date(now).setHours(23, 59, 59, 999)
                );
                setFilter((o) => ({ ...o, toDate, page: 1 }));
              }}
            />
          </div>

          <div>
            <label htmlFor="status">Tyno Status</label>
            <select
              id="status"
              className="p-1 h-8 rounded border focus:outline-none w-full"
              value={filter.status ?? ""}
              onChange={(e) =>
                setFilter((o) => ({
                  ...o,
                  status: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">All</option>
              {["sent", "pending", "parsed"].map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="attachment_category">Attachment Category</label>
            <select
              id="attachment_category"
              className="p-1 h-8 rounded border focus:outline-none w-full"
              value={filter.attachment_category ?? ""}
              onChange={(e) =>
                setFilter((o) => ({
                  ...o,
                  attachment_category: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">All</option>
              {[
                "PO",
                "Tax invoice",
                "Bank detail",
                "PAN",
                "GST certificate",
                "Other",
              ].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </FilterSheet>

        {data.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table className="w-full text-xs divide-y divide-gray-200 relative">
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th className="px-2 py-2 text-left">PO No.</th>
                      <th className="px-2 py-2 text-left">PO Date</th>
                      <th className="px-2 py-2 text-right">PO Amount</th>
                      <th className="px-2 py-2 text-left">
                        Attachment Category
                      </th>
                      <th className="px-2 py-2 text-left">Attachment</th>
                      <th className="px-2 py-2 text-left">Attachment Date</th>
                      <th className="px-2 py-2 text-left">Attachment Time</th>
                      <th className="px-2 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, i) => (
                      <tr className="border" key={i}>
                        {/* PO No */}
                        <td className="px-3 py-2 text-left font-medium">
                          {item.po_no ?? "--"}
                        </td>

                        {/* PO Date (Only Date) */}
                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          {item.date
                            ? moment(item.date).format("DD-MM-YYYY")
                            : "--"}
                        </td>

                        {/* PO Amount */}
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatAmountWithCommas(round(item.total, 2)) ?? "--"}
                        </td>

                        {/* Attachment Category */}
                        <td className="px-3 py-2 text-left">
                          {item.attachment?.category ?? "--"}
                        </td>

                        {/* Attachment URL (Clickable Link) */}
                        <td className="px-3 py-2 text-left">
                          {item.attachment?.url ? (
                            <a
                              href={item.attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 underline hover:text-blue-700"
                            >
                              Link
                            </a>
                          ) : (
                            "--"
                          )}
                        </td>

                        {/* Attachment Date (Only Date) */}
                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          {item.attachment?.created_at
                            ? moment(item.attachment.created_at).format(
                                "DD-MM-YYYY"
                              )
                            : "--"}
                        </td>

                        {/* Attachment Time (Only Time) */}
                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          {item.attachment?.created_at
                            ? moment(item.attachment.created_at).format(
                                "HH:mm:ss"
                              )
                            : "--"}
                        </td>
                        <td className="px-2 py-2 text-left">
                          {renderTynoStatus(item)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((item, i) => (
                <MobileRecordCard
                  key={i}
                  title={item.po_no ?? "--"}
                  status={renderTynoStatus(item)}
                  fields={[
                    {
                      label: "PO Date",
                      value: item.date
                        ? moment(item.date).format("DD-MM-YYYY")
                        : "--",
                    },
                    {
                      label: "PO Amount",
                      value:
                        formatAmountWithCommas(round(item.total, 2)) ?? "--",
                    },
                    {
                      label: "Attachment Category",
                      value: item.attachment?.category ?? "--",
                    },
                    {
                      label: "Attachment",
                      value: item.attachment?.url ? (
                        <a
                          href={item.attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline hover:text-blue-700"
                        >
                          Link
                        </a>
                      ) : (
                        "--"
                      ),
                    },
                    {
                      label: "Attachment Date",
                      value: item.attachment?.created_at
                        ? moment(item.attachment.created_at).format("DD-MM-YYYY")
                        : "--",
                    },
                    {
                      label: "Attachment Time",
                      value: item.attachment?.created_at
                        ? moment(item.attachment.created_at).format("HH:mm:ss")
                        : "--",
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
              <Pagination
                count={filter.count}
                itemCount={filter.count}
                page={filter.page}
                total={total}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No Data found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>
      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default PurchaseOrderReportListScreen;
